import { useEffect, useState } from 'react';
import { faThumbtack } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Typography } from '@design-system/components/typography/typography';
import { MentionText } from './MentionText';
import { Post, ReactionType } from '../model/post.types';
import { analytics } from '../lib/analytics';
import { useCommunityContext } from '../lib/communityContext';
import { useIsAuthorBlocked } from '../api/useIsAuthorBlocked';
import { useUpdatePost } from '../api/useUpdatePost';
import { EditContentForm } from './EditContentForm';
import { PostCardHeader } from './PostCardHeader';
import { PostMedia } from './PostMedia';
import { PostReactions } from './PostReactions';
import { PostFooter } from './PostFooter';
import { PostReplies } from './PostReplies';
import { Collapsible } from './Collapsible';

type Props = {
  post: Post;
  pinnedCount: number;
  showPinnedStyle?: boolean;
  onToggleReaction: (type: ReactionType) => Promise<unknown> | void;
};

export const PostCard = ({
  post,
  pinnedCount,
  showPinnedStyle,
  onToggleReaction,
}: Props) => {
  const { communityId, isContentEditEnabled } = useCommunityContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasExpanded, setHasExpanded] = useState(false);
  const [commentFocusKey, setCommentFocusKey] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [updatePost] = useUpdatePost();
  const saveEdit = (text: string) => {
    setIsEditing(false);
    void updatePost({
      variables: { communityId, postId: post.id, text },
      optimisticResponse: {
        updatePost: {
          __typename: 'Post',
          id: post.id,
          text,
          edited: post.edited,
        },
      },
    });
  };
  const openComposer = () => {
    analytics.trackCommentsExpanded('Add Comment');
    setIsExpanded(true);
    setCommentFocusKey((key) => key + 1);
  };
  const toggleComments = () => {
    if (!isExpanded) analytics.trackCommentsExpanded('Comment Count');
    setIsExpanded((expanded) => !expanded);
  };
  const handleToggleReaction = (type: ReactionType) => {
    const existing = post.reactions.find((reaction) => reaction.type === type);
    if (existing?.isToggled) {
      analytics.trackReactionRemoved(communityId, post);
    } else {
      analytics.trackReactionAdded(post);
    }
    return onToggleReaction(type);
  };

  // Mount the replies once expanded, then keep them mounted (hidden when
  // collapsed) so re-opening never refetches and flashes a loading state.
  useEffect(() => {
    if (isExpanded) setHasExpanded(true);
  }, [isExpanded]);
  const pinnedStyle = showPinnedStyle ?? post.pinned;
  const isAuthorBlocked = useIsAuthorBlocked(communityId);
  const authorBlocked = isAuthorBlocked(post.author.id);

  return (
    <article
      className={`flex w-full flex-col gap-4 p-4 ${
        pinnedStyle ? 'rounded-[10px] bg-[#EDE5FC]' : ''
      }`}
    >
      {pinnedStyle && (
        <div className="flex items-center gap-2 text-[#2C3548]">
          <FontAwesomeIcon icon={faThumbtack} className="text-[12px]" />
          <Typography
            as="span"
            variant="body"
            size="small"
            fontWeight="medium"
            className="uppercase tracking-[1px] text-[#2C3548]"
          >
            Pinned
          </Typography>
        </div>
      )}
      <PostCardHeader
        post={post}
        pinnedCount={pinnedCount}
        authorBlocked={authorBlocked}
        onEdit={
          isContentEditEnabled && post.author.isOwn
            ? () => setIsEditing(true)
            : undefined
        }
      />
      {isEditing ? (
        <EditContentForm
          initialText={post.content}
          onCancel={() => setIsEditing(false)}
          onSave={saveEdit}
        />
      ) : (
        <MentionText
          content={post.content}
          variant="body"
          size="large"
          fontWeight="normal"
          className="break-words"
        />
      )}
      {post.media.length > 0 && <PostMedia media={post.media} />}
      <PostReactions
        reactions={post.reactions}
        onToggle={handleToggleReaction}
      />
      <PostFooter
        commentCount={post.commentCount}
        isExpanded={isExpanded}
        onToggle={toggleComments}
        onComment={openComposer}
      />
      {hasExpanded && (
        <Collapsible expanded={isExpanded} className="-mt-4">
          <div className="pt-4">
            <PostReplies
              postId={post.id}
              commentCount={post.commentCount}
              commentFocusKey={commentFocusKey}
              onCommentAdded={() => analytics.trackCommentAdded(post)}
            />
          </div>
        </Collapsible>
      )}
    </article>
  );
};
